import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Eye, EyeOff, LogIn, Megaphone } from "lucide-react";
import InstallAppLink from "@/components/InstallAppLink";
import axios from "axios";
import { useLoginLook, accentOf, mediaUrl } from "@/lib/greetings";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "/rockpecker-logo.png";

/* ── Building hero card ────────────────────────────────────────────────────
 * Shared by the desktop right column and the mobile banner above the form.
 * `compact` = the mobile variant: shorter crop, smaller chip + caption, the
 * location line folds under the CoE pill instead of sitting beside it.
 * Defined at module scope on purpose — if it lived inside LoginPage the
 * <video> would remount on every keystroke and restart the loop.
 * ───────────────────────────────────────────────────────────────────────── */
/* The photo/video behind the card and the mobile masthead. `look.media` is an
 * admin-chosen picture (Admin → Greetings); none = the building video. Until
 * the first answer arrives on a fresh browser, show nothing but the dark
 * card, so a custom picture never flashes the building video first. */
function HeroMedia({ media, ready, className, style, label }) {
  if (!ready) return null;
  if (media && media.kind === "image") {
    return <img src={mediaUrl(media)} alt={label} className={className} style={style} draggable={false} />;
  }
  if (media && media.kind === "video") {
    return <video key={media.id} src={mediaUrl(media)} autoPlay loop muted playsInline preload="auto" aria-label={label} className={className} style={style} />;
  }
  return (
    <video
      src="/rockpecker-building-loop.mp4"
      poster="/rockpecker-building-poster.jpg"
      autoPlay loop muted playsInline preload="auto"
      aria-label={label}
      className={className}
      style={style}
    >
      <img
        src="/rockpecker-building-poster.jpg"
        alt="Rockpecker Centre of Excellence, Bhateri"
        className={className}
        draggable={false}
      />
    </video>
  );
}

function HeroCard({ compact = false, look, ready }) {
  // A live greeting takes over the caption (headline + one line in its colour).
  const greeting = look?.greeting && look.greeting.show_on !== "app" ? look.greeting : null;
  const accent = greeting ? accentOf(greeting.accent) : null;
  return (
    <div
      className={`relative w-full overflow-hidden rl-rise ${compact ? "aspect-[16/9] rounded-2xl" : "aspect-[16/10] rounded-3xl"}`}
      style={{
        background: "#334155",
        border: "1px solid rgba(255,255,255,0.6)",
        boxShadow: compact
          ? "inset 0 1px 0 rgba(255,255,255,0.7), 0 10px 24px rgba(15,23,42,0.14)"
          : "inset 0 1px 0 rgba(255,255,255,0.7), 0 18px 44px rgba(15,23,42,0.18), 0 2px 6px rgba(15,23,42,0.08)",
      }}
    >
      <HeroMedia
        media={look?.media}
        ready={ready}
        label="Rockpecker Centre of Excellence"
        className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
      />

      {/* Rockpecker Metallic mark — frosted chip, top-left */}
      <div
        data-testid={compact ? "login-logo-mobile" : "login-logo"}
        className={`login-brand-chip absolute rounded-xl ${compact ? "top-3 left-3 px-2.5 py-1.5" : "top-5 left-5 px-3.5 py-2"}`}
      >
        <img
          src={LOGO_URL}
          alt="Rockpecker Metallic"
          className={`${compact ? "h-5" : "h-7"} w-auto object-contain select-none`}
          draggable={false}
        />
      </div>

      {/* Legibility scrim + caption row */}
      <div className={`login-scrim absolute inset-x-0 bottom-0 pointer-events-none ${compact ? "h-[58%]" : "h-[46%]"}`} />
      <div className={`absolute inset-x-0 bottom-0 flex items-end justify-between ${compact ? "gap-3 px-4 pb-3.5" : "gap-6 px-7 pb-6"}`}>
        {greeting ? (
          <div className="min-w-0" data-testid="login-greeting">
            <p className={`font-bold leading-tight ${compact ? "text-[17px]" : "text-3xl"}`} style={{ color: "#fff" }}>
              {greeting.greeting}
            </p>
            {greeting.message && (
              <p className={`leading-snug ${compact ? "text-[11px] mt-0.5" : "text-[15px] mt-1.5 max-w-[560px]"}`} style={{ color: accent.light }}>
                {greeting.message}
              </p>
            )}
          </div>
        ) : (
          <p className={`font-bold leading-tight ${compact ? "text-[17px]" : "text-3xl"}`} style={{ color: "#fff" }}>
            Precision Engineering.<br />
            <span style={{ color: "rgba(255,255,255,0.68)" }}>Delivered on time.</span>
          </p>
        )}
        <div className="flex flex-col items-end shrink-0">
          <div
            className={`inline-flex items-center rounded-full ${compact ? "gap-1.5 px-2.5 py-1 mb-1" : "gap-2 px-3.5 py-1.5 mb-2"}`}
            style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.28)" }}
          >
            <span className={`rounded-full bg-amber-400 animate-pulse ${compact ? "w-1.5 h-1.5" : "w-2 h-2"}`} style={{ boxShadow: "0 0 6px rgba(245,158,11,0.7)" }} />
            <span className={`font-semibold tracking-widest uppercase ${compact ? "text-[9px]" : "text-xs"}`} style={{ color: "#fde68a" }}>
              Centre of Excellence
            </span>
          </div>
          <p className={compact ? "text-[10px]" : "text-sm"} style={{ color: "rgba(255,255,255,0.60)" }}>Bhateri, Punjab, India</p>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login, loginWithOtp } = useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const { look, ready: lookReady } = useLoginLook();
  const loginGreeting = look?.greeting && look.greeting.show_on !== "app" ? look.greeting : null;
  const [mode, setMode] = useState("password");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpInfo, setOtpInfo] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const otpRefs = useRef([]);

  // The login page is designed for light mode only. The app's theme class lives
  // on <html>; force it to `light` while this page is mounted so the theme-aware
  // .rl-canvas / .rl-glass surfaces resolve to their light variants, then restore
  // the user's real theme on unmount (i.e. once they're signed in).
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.className;
    root.className = "light";
    return () => { root.className = prev; };
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/api/public/announcement`)
      .then(res => {
        const { text, show_on } = res.data || {};
        if (text && (show_on === "login" || show_on === "both")) {
          setAnnouncement(text);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn(r => r - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else setError("Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpRequest = async () => {
    setError("");
    if (!email.trim()) { setError("Email is required."); return; }
    setOtpSending(true);
    try {
      const res = await axios.post(`${API_BASE}/api/auth/otp/request`, { email: email.trim() });
      setOtpInfo(res.data.message);
      setOtpDigits(["", "", "", "", "", ""]);
      setResendIn(60);
      setMode("otp-verify");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else setError("Failed to send code. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpVerify = async () => {
    setError("");
    const code = otpDigits.join("");
    if (otpDigits.some(d => d === "")) { setError("Please enter the 6-digit code."); return; }
    setOtpVerifying(true);
    try {
      await loginWithOtp(email.trim(), code);
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else setError("Invalid or expired code. Please try again.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleOtpDigit = (idx, val) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    setOtpDigits(prev => { const next = [...prev]; next[idx] = digit; return next; });
    if (digit && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) {
      setOtpDigits(prev => { const next = [...prev]; next[idx - 1] = ""; return next; });
      otpRefs.current[idx - 1]?.focus();
    }
  };

  // Carved-field vocabulary (depth pass): the .login-field class on the WRAPPER
  // carries the recessed carve + focus-within wake; inputs inside are transparent.
  const innerInputCls = "relative w-full h-11 px-4 text-sm bg-transparent border-none outline-none text-[#0f172a] disabled:opacity-50";

  // ── Email ghost-text autocomplete ──────────────────────────────────────────
  // Suggests the company domain as greyed-out text after the caret (Gmail-style).
  // The real input value is never mutated mid-typing; the suffix is only added
  // when the user accepts via Tab / Enter / Right-arrow. Suggestion hides once
  // the user types their own "@", so non-rockpecker emails work freely.
  const EMAIL_SUFFIX = "@rockpecker.com";
  const emailSuffixHint = (email && !email.includes("@")) ? EMAIL_SUFFIX : "";

  const acceptEmailSuffix = () => {
    if (!emailSuffixHint) return false;
    setEmail(email + EMAIL_SUFFIX);
    return true;
  };

  const handleEmailKeyDown = (e) => {
    if (!emailSuffixHint) return; // nothing to accept → let default happen (e.g. Enter submits)
    const atEnd = e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length;
    if (e.key === "Tab" || e.key === "Enter" || (e.key === "ArrowRight" && atEnd)) {
      e.preventDefault();
      acceptEmailSuffix();
    }
  };

  const ErrorBox = () => error ? (
    <div
      className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm"
      style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#f87171" }}
      data-testid="login-error"
    >{error}</div>
  ) : null;

  const PrimaryBtn = ({ children, disabled, onClick, type = "button", testId }) => (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="w-full h-11 flex items-center justify-center gap-2 rounded-lg text-sm font-semibold mt-2 rl-lit-btn bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid={testId}
    >
      {children}
    </button>
  );

  const Spinner = () => (
    <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: "rgba(255,255,255,0.3)", borderTopColor: "#fff" }} />
  );

  const GhostLink = ({ children, onClick, testId, muted }) => (
    <button
      type="button"
      onClick={onClick}
      className="text-sm transition-opacity hover:opacity-80"
      style={{ color: muted ? "#475569" : "#f97316", background: "none", border: "none", cursor: "pointer" }}
      data-testid={testId}
    >
      {children}
    </button>
  );

  return (
    <div className="rl-canvas min-h-screen w-full flex justify-center" data-testid="login-page">
      <style>{`
        @keyframes rl-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .rl-rise { opacity: 0; animation: rl-rise 0.55s cubic-bezier(0.2,0.7,0.2,1) forwards; }

        /* Carved field — login-local mirror of .light .rl-search, but driven by
         * :focus-within so the carve can live on a WRAPPER (the email field needs
         * a ghost-text overlay behind a transparent input). Recessed at rest,
         * wakes on focus with the orange ring. */
        .login-field {
          background-color: hsl(var(--muted) / 0.5);
          border: 1px solid transparent;
          box-shadow: inset 0 1px 2.5px rgba(40, 30, 20, 0.12);
          transition: background-color 150ms ease, box-shadow 150ms ease, border-color 150ms ease;
        }
        .login-field:focus-within {
          background-color: hsl(var(--card));
          border-color: rgba(249, 115, 22, 0.5);
          box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.10), 0 2px 6px rgba(40, 30, 20, 0.08);
        }

        /* Bottom scrim on the building video — lets the white caption read over
         * moving footage. Performance Mode swaps the gradient for a solid tint
         * (legibility must survive the flat pass). */
        .login-scrim {
          background-image: linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0.40) 45%, rgba(15,23,42,0.78) 100%);
        }

        /* ── Mobile masthead ──────────────────────────────────────────────
         * Full-bleed video header. Lives in normal flow (so nothing below it
         * needs magic offsets) but breaks out of the panel's side padding via
         * a viewport-width negative margin. Height is viewport-relative and
         * clamped so it never eats the sign-in card on a small phone. */
        .login-masthead {
          position: relative;
          width: 100vw;
          margin-left: calc(50% - 50vw);
          margin-right: calc(50% - 50vw);
          height: clamp(190px, 30vh, 280px);
          margin-bottom: -14px; /* the fade's tail already reads as space */
          overflow: hidden;
        }
        /* Two stops of white before the hard landing: the image loses contrast
         * early, then meets the canvas colour exactly at 100% — a visible seam
         * is what makes a header feel pasted on. */
        .login-masthead-fade {
          background-image: linear-gradient(
            180deg,
            rgba(0,0,0,0.10) 0%,
            rgba(0,0,0,0.00) 26%,
            hsl(var(--background) / 0) 46%,
            hsl(var(--background) / 0.72) 78%,
            hsl(var(--background)) 100%
          );
        }
        html[data-flat="1"] .login-masthead-fade {
          background-image: linear-gradient(180deg, hsl(var(--background) / 0) 55%, hsl(var(--background)) 100%);
        }

        /* Frosted brand chip pinned to the video card (Rockpecker Metallic mark). */
        .login-brand-chip {
          background-color: rgba(255,255,255,0.80);
          -webkit-backdrop-filter: blur(12px) saturate(1.4);
          backdrop-filter: blur(12px) saturate(1.4);
          border: 1px solid rgba(255,255,255,0.65);
          box-shadow: 0 4px 12px rgba(15,23,42,0.18), inset 0 1px 0 rgba(255,255,255,0.85);
        }

        html[data-flat="1"] .login-field,
        html[data-flat="1"] .login-field:focus-within,
        html[data-flat="1"] .login-brand-chip {
          box-shadow: none !important;
        }
        html[data-flat="1"] .login-brand-chip {
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
          background-color: rgba(255,255,255,0.92);
        }
        html[data-flat="1"] .login-scrim {
          background-image: none;
          background-color: rgba(15,23,42,0.55);
        }
        @media (prefers-reduced-motion: reduce) {
          .rl-rise { animation: none; opacity: 1; }
        }
      `}</style>

      {/* Content frame — keeps the panel and the hero from drifting apart on
          ultra-wide displays; everything below stays a two-column flex row. */}
      <div className="flex w-full max-w-[1560px]">

      {/* ── Left — Login panel. ONE brand lockup (RL² icon + wordmark) sits with
          the vertically-centred form; the Rockpecker Metallic mark now lives on
          the video card (its own building), footer pinned to the corner. ── */}
      <div className="relative w-full lg:w-[470px] flex-shrink-0 flex flex-col justify-center items-center lg:items-stretch px-6 sm:px-10 lg:px-12 pt-0 pb-10 lg:py-14 z-10">

        {/* ── Mobile masthead (below lg only) ──────────────────────────────
            Not a card. The building runs edge-to-edge off the top of the
            viewport and DISSOLVES into the canvas, so the page reads as one
            surface with a photographic header rather than a tile stacked on
            a lockup. It carries no caption and no RockLink mark — the only
            thing on it is the Metallic sign, which is already physically on
            that building. The words all live once, below, in the lockup. ── */}
        <div className="login-masthead lg:hidden" aria-hidden="false">
          <HeroMedia
            media={look?.media}
            ready={lookReady}
            label="Rockpecker Centre of Excellence, Bhateri"
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
            style={{ objectPosition: "50% 42%" }}
          />
          {/* Dissolve — video → canvas. Ends on the exact canvas colour so
              there is no seam where the band stops. */}
          <div className="login-masthead-fade absolute inset-0 pointer-events-none" />
          <div data-testid="login-logo-mobile" className="login-brand-chip absolute top-4 left-6 rounded-lg px-2.5 py-1.5">
            <img src={LOGO_URL} alt="Rockpecker Metallic" className="h-5 w-auto object-contain select-none" draggable={false} />
          </div>
        </div>

        {/* Middle: Form — centred */}
        <div className="flex flex-col justify-center max-w-sm w-full">
          <div className="mb-6 mt-1 lg:mt-0 rl-rise">
            {/* Brand lockup — tall RL badge spans both the wordmark and the
                subtitle; wordmark + snug subtitle stacked beside it (mirrors the
                in-app header lockup). */}
            <div className="flex items-center gap-3.5">
              {/* Icon carries its own rounded glass edge — no wrapper border/clip,
                  just a soft drop shadow for lift (prevents a double white rim). */}
              <img
                src="/favicon-rl2-v2.png"
                alt="RockLink"
                className="w-[58px] h-[58px] shrink-0 select-none"
                style={{ filter: "drop-shadow(0 6px 16px rgba(239,68,68,0.22))" }}
                draggable={false}
              />
              <div className="flex flex-col">
                {/* The RL² icon already carries the "²" — no version chip needed. */}
                <h1
                  className="text-[38px] font-extrabold leading-none tracking-tight"
                  style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
                >
                  <span style={{ color: "#ef4444" }}>Rock</span><span style={{ color: "#f97316" }}>Link</span>
                </h1>
                <p
                  className="text-[11px] font-bold uppercase mt-1.5 leading-none"
                  style={{ color: "#64748b", letterSpacing: "0.19em" }}
                >
                  Production Command Centre
                </p>
              </div>
            </div>

            {/* Phones: the masthead carries no caption, so a live greeting
                sits under the lockup instead. */}
            {loginGreeting && (
              <p className="lg:hidden mt-4 text-[13px] leading-snug" data-testid="login-greeting-mobile">
                <strong style={{ color: accentOf(loginGreeting.accent).ink }}>{loginGreeting.greeting}</strong>
                {loginGreeting.message && <span style={{ color: "#64748b" }}> · {loginGreeting.message}</span>}
              </p>
            )}

          </div>

          {/* ── Password mode ── */}
          {mode === "password" && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div className="rl-rise rl-glass space-y-4" style={{ animationDelay: "0.12s", borderRadius: 14, padding: 18 }}>
              {/* Card heading — relocated from the page header */}
              <p className="text-[13px] font-medium" style={{ color: "#334155" }}>Sign in to access your workspace</p>
              {/* Email */}
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: "#475569" }}>Email</label>
                <div className="login-field relative h-11 rounded-lg">
                  {/* Ghost suffix overlay — mirrors the input's typography/padding exactly */}
                  <div className="absolute inset-0 flex items-center px-4 text-sm pointer-events-none whitespace-pre overflow-hidden rounded-lg" aria-hidden="true">
                    <span className="invisible">{email}</span>
                    <span style={{ color: "#cbd5e1" }}>{emailSuffixHint}</span>
                  </div>
                  <input
                    type="email" autoComplete="email" value={email}
                    onChange={e => setEmail(e.target.value)} onKeyDown={handleEmailKeyDown} disabled={loading}
                    placeholder="you@rockpecker.com"
                    className={innerInputCls}
                    data-testid="login-email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label
                  className="block text-xs font-medium uppercase tracking-widest mb-1.5"
                  style={{ color: "#475569" }}
                >
                  Password
                </label>
                <div className="login-field relative h-11 rounded-lg">
                  <input
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={loading}
                    placeholder="••••••••"
                    className={`${innerInputCls} pr-11`}
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: "#475569" }}
                    onMouseEnter={e => e.target.style.color = "#94a3b8"}
                    onMouseLeave={e => e.target.style.color = "#475569"}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <ErrorBox />

              <PrimaryBtn type="submit" disabled={loading} testId="login-submit">
                {loading ? <Spinner /> : <><LogIn className="w-4 h-4" />Sign In</>}
              </PrimaryBtn>
              </div>

              <div className="text-center pt-1">
                <GhostLink
                  onClick={() => { setMode("otp-request"); setError(""); }}
                  testId="switch-to-otp"
                >
                  Sign in with email code
                </GhostLink>
              </div>
            </form>
          )}

          {/* ── OTP request mode ── */}
          {mode === "otp-request" && (
            <div className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest mb-1.5" style={{ color: "#475569" }}>Email</label>
                <div className="login-field relative h-11 rounded-lg">
                  {/* Ghost suffix overlay — mirrors the input's typography/padding exactly */}
                  <div className="absolute inset-0 flex items-center px-4 text-sm pointer-events-none whitespace-pre overflow-hidden rounded-lg" aria-hidden="true">
                    <span className="invisible">{email}</span>
                    <span style={{ color: "#cbd5e1" }}>{emailSuffixHint}</span>
                  </div>
                  <input
                    type="email" autoComplete="email" value={email}
                    onChange={e => setEmail(e.target.value)} onKeyDown={handleEmailKeyDown} disabled={otpSending}
                    placeholder="you@rockpecker.com"
                    className={innerInputCls}
                    data-testid="otp-email"
                  />
                </div>
              </div>
              <ErrorBox />
              <PrimaryBtn onClick={handleOtpRequest} disabled={otpSending} testId="otp-request-submit">
                {otpSending ? <Spinner /> : "Email me a code"}
              </PrimaryBtn>
              <div className="text-center pt-1">
                <GhostLink
                  onClick={() => { setMode("password"); setError(""); }}
                  testId="switch-to-password"
                >
                  Sign in with password instead
                </GhostLink>
              </div>
            </div>
          )}

          {/* ── OTP verify mode ── */}
          {mode === "otp-verify" && (
            <div className="space-y-4">
              {otpInfo && (
                <p className="text-sm" style={{ color: "#94a3b8" }} data-testid="otp-info">{otpInfo}</p>
              )}
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest mb-2" style={{ color: "#475569" }}>
                  Sign-in Code
                </label>
                <div className="flex gap-2">
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <input
                      key={i}
                      ref={el => { otpRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]"
                      maxLength={1}
                      value={otpDigits[i]}
                      onChange={e => handleOtpDigit(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      disabled={otpVerifying}
                      className="login-field w-11 h-11 text-center text-lg font-semibold rounded-lg outline-none text-[#0f172a] disabled:opacity-50"
                      data-testid={`otp-digit-${i}`}
                    />
                  ))}
                </div>
              </div>
              <ErrorBox />
              <PrimaryBtn
                onClick={handleOtpVerify}
                disabled={otpVerifying || otpDigits.some(d => d === "")}
                testId="otp-verify-submit"
              >
                {otpVerifying ? <Spinner /> : "Verify & Sign In"}
              </PrimaryBtn>
              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={handleOtpRequest}
                  disabled={resendIn > 0 || otpSending}
                  className="text-sm transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ color: "#f97316", background: "none", border: "none", cursor: resendIn > 0 ? "default" : "pointer" }}
                  data-testid="otp-resend"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
                <GhostLink
                  onClick={() => { setMode("otp-request"); setError(""); setOtpDigits(["", "", "", "", "", ""]); }}
                  testId="otp-different-email"
                  muted
                >
                  Use a different email
                </GhostLink>
              </div>
            </div>
          )}

          {/* Announcement — demoted to a quiet note under the form. Admin-set
              via settings; renders in every auth mode when present. No bubble,
              no border: small muted amber text that wraps if it must. */}
          {announcement && (
            <div
              className="flex items-start gap-2 mt-5 px-1"
              data-testid="login-announcement"
              style={{ color: "#a16207", fontSize: 12, lineHeight: 1.55, fontWeight: 500 }}
            >
              <Megaphone className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ opacity: 0.7 }} />
              <span>{announcement}</span>
            </div>
          )}
        </div>

        {/* Bottom: footer — pinned to the corner */}
        <div className="mt-10 w-full max-w-sm lg:mt-0 lg:absolute lg:bottom-10 lg:left-12 lg:w-auto lg:max-w-none flex flex-col gap-1.5">
          <InstallAppLink className="text-xs" />
          <p className="text-xs" style={{ color: "#94a3b8" }}>
            © 2026 Rockpecker Pvt Ltd · All rights reserved
          </p>
        </div>
      </div>

      {/* ── Right — Building hero (layout B): the caption lives ON the video
          card over a bottom scrim — headline bottom-left, CoE pill + location
          bottom-right — and the Rockpecker Metallic mark sits top-left as a
          frosted chip (the logo on its own building). Nothing detached below. ── */}
      <div className="hidden lg:flex flex-1 relative items-center justify-center pl-4 pr-12 py-14">
        <div className="w-full max-w-[1040px]">
          <HeroCard look={look} ready={lookReady} />
        </div>
      </div>
      </div>
    </div>
  );
}
